import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, ArrowLeft, Mail, User, MessageSquare } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("contact.fillRequired") || "Täytä kaikki pakolliset kentät");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(t("contact.sent") || "Viesti lähetetty!");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("contact.error") || "Viestin lähetys epäonnistui");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f8ff] via-[#f0f0ff] to-[#e8e8f8]">
      {/* Header */}
      <header className="bg-white/40 backdrop-blur-md border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="rounded-full"
              data-testid="contact-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t("common.back") || "Takaisin"}
            </Button>
            <h1 className="font-heading text-xl text-void">{t("contact.title") || "Ota yhteyttä"}</h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="font-body text-void hover:text-void/80"
          >
            Uneksia
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {sent ? (
          <div className="text-center py-16" data-testid="contact-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <Mail className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="font-heading text-3xl text-void mb-4">
              {t("contact.thankYou") || "Kiitos viestistäsi!"}
            </h2>
            <p className="text-void/60 font-body text-lg mb-8">
              {t("contact.thankYouDesc") || "Olemme vastaanottaneet viestisi ja vastaamme mahdollisimman pian. Tarkista sähköpostisi vahvistusta varten."}
            </p>
            <Button
              onClick={() => navigate("/")}
              className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-3 font-body"
              data-testid="contact-home-btn"
            >
              {t("contact.backToHome") || "Takaisin etusivulle"}
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl text-void mb-3">
                {t("contact.heading") || "Ota meihin yhteyttä"}
              </h2>
              <p className="text-void/60 font-body text-lg">
                {t("contact.description") || "Haluatko kysyä jotain, antaa palautetta tai ehdottaa uusia ominaisuuksia? Lähetä meille viesti!"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="glass-effect rounded-2xl p-6 space-y-4">
                <div>
                  <label className="block text-sm font-body text-void/70 mb-1.5">
                    <User className="w-3.5 h-3.5 inline mr-1" />
                    {t("contact.name") || "Nimi"} *
                  </label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("contact.namePlaceholder") || "Nimesi"}
                    className="bg-white/60 border-white/80 rounded-xl"
                    data-testid="contact-name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body text-void/70 mb-1.5">
                    <Mail className="w-3.5 h-3.5 inline mr-1" />
                    {t("contact.email") || "Sähköposti"} *
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t("contact.emailPlaceholder") || "sinun@email.com"}
                    className="bg-white/60 border-white/80 rounded-xl"
                    data-testid="contact-email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body text-void/70 mb-1.5">
                    {t("contact.subject") || "Aihe"}
                  </label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder={t("contact.subjectPlaceholder") || "Mistä haluaisit puhua?"}
                    className="bg-white/60 border-white/80 rounded-xl"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body text-void/70 mb-1.5">
                    <MessageSquare className="w-3.5 h-3.5 inline mr-1" />
                    {t("contact.message") || "Viesti"} *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t("contact.messagePlaceholder") || "Kirjoita viestisi tähän..."}
                    rows={6}
                    className="w-full bg-white/60 border border-white/80 rounded-xl p-3 font-body text-void resize-none focus:outline-none focus:ring-2 focus:ring-lucid/50"
                    data-testid="contact-message"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-void text-white hover:bg-void/90 rounded-full py-3 font-body text-base disabled:opacity-50"
                data-testid="contact-submit"
              >
                {sending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                    {t("contact.sending") || "Lähetetään..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    {t("contact.send") || "Lähetä viesti"}
                  </span>
                )}
              </Button>

              <p className="text-xs text-void/40 text-center font-body">
                {t("contact.privacy") || "Käsittelemme tietojasi luottamuksellisesti. Lue lisää"}{" "}
                <a href="/privacy" className="text-lucid hover:underline">{t("contact.privacyLink") || "tietosuojasivultamme"}</a>.
              </p>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
